import { AlertTriangle, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";

import { useGoalModels, useLatestAnalysis, useStartAnalysis } from "../api";

/**
 * Write-only "AI insights" panel inside the goal detail drawer. Pick a local
 * model, run a background analysis, and see the three deliverables (current
 * progress, clarified success criteria, recommendations). The run is persisted
 * server-side and polled, so the user can close the drawer and come back.
 */
export function GoalAnalysisPanel({
  goalId,
  goalTitle,
  openwebuiUrl,
}: {
  goalId: string;
  goalTitle: string;
  openwebuiUrl?: string | null;
}) {
  const models = useGoalModels();
  const latest = useLatestAnalysis(goalId);
  const start = useStartAnalysis(goalId);
  const [model, setModel] = useState("");
  const [direction, setDirection] = useState("");

  // Default the picker to the server's default model once it loads.
  useEffect(() => {
    if (!model && models.data?.default_model) setModel(models.data.default_model);
  }, [models.data, model]);

  const analysis = latest.data ?? null;
  const inFlight =
    start.isPending || analysis?.status === "pending" || analysis?.status === "running";
  const configured = models.data?.configured ?? true;
  const available = models.data?.models ?? [];
  const noModels = configured && available.length === 0 && !models.isLoading;

  /** Open Cinch AI (Open WebUI) in a new tab, copying a ready-to-send prompt
   *  that drives the Company Goals tool against this goal. */
  function openInCinchAi() {
    if (!openwebuiUrl) return;
    const prompt =
      `Using the Cinch Ops "Company Goals" tool, analyze the goal "${goalTitle}" ` +
      `(id ${goalId}): assess current progress, clarify its success criteria against the ` +
      `data, and recommend concrete next moves.`;
    try {
      void navigator.clipboard?.writeText(prompt);
    } catch {
      /* clipboard may be blocked; opening the tab is the important part */
    }
    window.open(openwebuiUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <section className="border-t border-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" /> AI insights
        </h3>
        <div className="flex items-center gap-2">
          <select
            aria-label="Local model"
            className="h-8 max-w-[12rem] rounded-xl border border-input bg-background px-2 text-xs"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={inFlight || !configured || available.length === 0}
          >
            {available.length === 0 ? (
              <option value="">No local models</option>
            ) : (
              available.map((m) => (
                <option key={m.name} value={m.name}>
                  {m.name}
                  {m.is_default ? " (default)" : ""}
                </option>
              ))
            )}
          </select>
          <Button
            size="sm"
            onClick={() =>
              start.mutate({ model: model || null, direction: direction.trim() || null })
            }
            disabled={inFlight || !configured || available.length === 0}
          >
            {inFlight ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Analyzing…
              </>
            ) : analysis ? (
              "Re-run"
            ) : (
              "Run analysis"
            )}
          </Button>
        </div>
      </div>

      <textarea
        value={direction}
        onChange={(e) => setDirection(e.target.value)}
        disabled={inFlight}
        placeholder="Optional — steer the agent on where to look (e.g. 'count integrations built, then installs via INTEGRATION_TO_ACCOUNT, then transactions by entity_source')."
        className="mt-2 min-h-[48px] w-full resize-y rounded-xl border border-input bg-background px-3 py-2 text-xs"
      />

      {!configured || noModels ? (
        <p className="mt-2 text-xs text-muted-foreground">
          No local LLM is reachable right now. Start Ollama (or check the host) and try again.
        </p>
      ) : null}

      {inFlight ? (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-surface-raised/50 px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" />
          Querying the data and analyzing this goal… this can take a few minutes. It runs in the
          background — you can close this and come back.
        </div>
      ) : null}

      {!inFlight && analysis?.status === "failed" ? (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-danger/40 bg-danger-light/60 px-3 py-2 text-xs font-medium text-danger">
          <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" />
          <span>{analysis.error ?? "Analysis failed. Try again."}</span>
        </div>
      ) : null}

      {!inFlight && analysis?.status === "complete" ? (
        <div className="mt-3 flex flex-col gap-3 text-sm">
          {analysis.progress_assessment ? (
            <div>
              <div className="text-xs font-semibold text-foreground">Current progress</div>
              <p className="mt-0.5 leading-relaxed text-foreground/90">
                {analysis.progress_assessment}
              </p>
            </div>
          ) : null}
          {analysis.clarified_success_criteria ? (
            <div>
              <div className="text-xs font-semibold text-foreground">
                Clarified success criteria
              </div>
              <p className="mt-0.5 leading-relaxed text-foreground/90">
                {analysis.clarified_success_criteria}
              </p>
            </div>
          ) : null}
          {analysis.recommendations.length > 0 ? (
            <div>
              <div className="text-xs font-semibold text-foreground">Recommendations</div>
              <ul className="mt-1 list-disc space-y-1 pl-5 text-foreground/90">
                {analysis.recommendations.map((rec, i) => (
                  <li key={`rec-${i}`}>{rec}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {analysis.queries.length > 0 ? (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">
                Data the agent queried ({analysis.queries.length})
              </summary>
              <ul className="mt-1 space-y-1">
                {analysis.queries.map((q, i) => (
                  <li key={`q-${i}`} className="flex items-start gap-1.5">
                    <span className={q.ok ? "text-success" : "text-danger"}>
                      {q.ok ? "✓" : "✗"}
                    </span>
                    <code className="break-all rounded bg-surface-raised px-1 py-0.5 text-[11px] text-foreground/80">
                      {q.sql}
                    </code>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <p className="text-[11px] text-muted-foreground">
            Generated by {analysis.model ?? "a local LLM"} · grounded in this goal's data + linked
            objectives. For deeper, live exploration, use Cinch AI.
          </p>
        </div>
      ) : null}

      {!inFlight && !analysis ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Run a data-aware analysis — the agent queries Snowflake + this goal's linked objectives
          for a critical-but-fair read of progress, sharpened success criteria, and concrete
          recommendations. Add a direction above to point it at specific data.
        </p>
      ) : null}

      {openwebuiUrl ? (
        <button
          type="button"
          onClick={openInCinchAi}
          title="Copies a goal-analysis prompt and opens Cinch AI (Open WebUI) in a new tab"
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Continue in Cinch AI
        </button>
      ) : null}
    </section>
  );
}
